/**
 * Daily Pricing tab — Sprint 1 (DEC-045 · PIC-105).
 *
 * Workflow-first design (per PO direction 2026-02-04):
 *   1. Current Pricing Summary — what does today look like relative to yesterday?
 *   2. Post Today's Prices     — the actual data-entry moment.
 *   3. Validation Before Posting — protect against early-morning mistakes.
 *   4. Today's Posting Summary — immediate confirmation the job is done.
 *
 * Business rules preserved end-to-end:
 *   • Single business-wide rack per product.
 *   • Timestamp-based effective dating.
 *   • CAD per litre. Decimal precision (strings on the wire).
 *   • Owner + Administrator may post. Others read-only.
 *   • Corrections permitted; no deletion; audit trail preserved by the API.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CurrencyDollar, ArrowUp, ArrowDown, Minus, Warning, CheckCircle,
  Clock, PaperPlaneTilt, ArrowsClockwise,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";

const PRODUCTS = [
  { code: "regular",      label: "Regular Gas" },
  { code: "premium",      label: "Premium Gas" },
  { code: "clear_diesel", label: "Clear Diesel" },
  { code: "dyed_diesel",  label: "Dyed Diesel" },
  { code: "def",          label: "DEF" },
];

const dpApi = {
  current:      () => api.get("/daily-pricing/rack/current"),
  latest:       () => api.get("/daily-pricing/rack/postings/latest"),
  validate:     (body) => api.post("/daily-pricing/rack/validate", body),
  postBatch:    (body) => api.post("/daily-pricing/rack/batch", body),
  history:      (params) => api.get("/daily-pricing/rack/history", { params }),
};

function fmtDT(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}
function fmtRel(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return "—";
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff/60)}m ago`;
  if (diff < 86400) return `${Math.round(diff/3600)}h ago`;
  return `${Math.round(diff/86400)}d ago`;
}
const isValidDec = (s) => /^\d+(\.\d{1,6})?$/.test((s ?? "").toString().trim());
const dashOr = (v) => v ?? "—";

/* ----------- Direction arrow ----------- */
function DirectionArrow({ direction, change, pct }) {
  if (!direction || direction === "flat") {
    return (
      <span className="inline-flex items-center gap-1 text-slate-500" data-testid="dp-direction-flat">
        <Minus size={14} /> —
      </span>
    );
  }
  const up = direction === "up";
  return (
    <span
      className={`inline-flex items-center gap-1 font-semibold ${up ? "text-rose-700" : "text-emerald-700"}`}
      data-testid={`dp-direction-${direction}`}
      title={`Change ${change} · ${pct}%`}
    >
      {up ? <ArrowUp size={14} weight="bold" /> : <ArrowDown size={14} weight="bold" />}
      {change} ({pct}%)
    </span>
  );
}

/* ================================================================== */
/*  Section 1 — Current Pricing Summary                                */
/* ================================================================== */
function CurrentSummary({ data, loading }) {
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="dp-summary">
      <header className="px-5 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800">Today's Selling Prices (Cardlock)</h3>
        <p className="text-xs text-slate-500">CAD per litre · the price charged to customer accounts today</p>
      </header>
      {loading ? (
        <div className="p-4 text-sm text-slate-500">Loading…</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs">
              <tr>
                <th className="px-4 py-2 text-left font-medium">Product</th>
                <th className="px-4 py-2 text-right font-medium">Yesterday</th>
                <th className="px-4 py-2 text-right font-medium">Today</th>
                <th className="px-4 py-2 text-right font-medium">Change</th>
                <th className="px-4 py-2 text-left font-medium">Effective</th>
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((it) => (
                <tr key={it.product_code} className="border-t border-slate-100" data-testid={`dp-row-${it.product_code}`}>
                  <td className="px-4 py-2 font-medium text-slate-800">{it.product_label}</td>
                  <td className="px-4 py-2 text-right text-slate-600" data-testid={`dp-prev-${it.product_code}`}>
                    {dashOr(it.previous?.price_per_litre)}
                  </td>
                  <td className="px-4 py-2 text-right font-semibold text-slate-900" data-testid={`dp-current-${it.product_code}`}>
                    {dashOr(it.current?.price_per_litre)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {it.current?.price_per_litre ? (
                      <DirectionArrow direction={it.direction} change={it.change} pct={it.pct_change} />
                    ) : <span className="text-slate-400" data-testid={`dp-nopost-${it.product_code}`}>Not posted</span>}
                  </td>
                  <td className="px-4 py-2 text-slate-600 text-xs">{fmtDT(it.current?.effective_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/* ================================================================== */
/*  Section 2/3 — Post Today's Prices + Inline Validation              */
/* ================================================================== */
function PostForm({ canWrite, currentData, onPosted }) {
  const [values, setValues] = useState(() =>
    Object.fromEntries(PRODUCTS.map((p) => [p.code, ""]))
  );
  const [notes, setNotes] = useState("");
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [posting, setPosting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const missing = PRODUCTS.filter((p) => !isValidDec(values[p.code]));
  const canValidate = missing.length === 0 && canWrite;
  const missingLabels = missing.map((p) => p.label);
  const missingMessage = missing.length === 0
    ? ""
    : missing.length === 1
      ? `${missingLabels[0]} price is required before posting.`
      : `Prices required: ${missingLabels.join(" · ")}`;

  const setVal = (code, v) => {
    setValues((prev) => ({ ...prev, [code]: v }));
    setValidation(null);   // clear stale validation
    setConfirming(false);
  };

  const buildLines = () => PRODUCTS
    .filter((p) => isValidDec(values[p.code]))
    .map((p) => ({ product_code: p.code, price_per_litre: values[p.code].trim(), notes: notes.trim() || null }));

  const runValidate = async () => {
    setValidating(true);
    try {
      const { data } = await dpApi.validate({ lines: buildLines() });
      setValidation(data);
      if (data.warnings?.some((w) => w.kind === "unusual_change")) {
        setConfirming(true);
      } else {
        setConfirming(false);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Validation failed");
    } finally { setValidating(false); }
  };

  const runPost = async () => {
    // Sprint-1.0.2 · P0 · Diagnostic + user-facing feedback.
    // The button MUST never silently do nothing.
    // Log at every stage, and always surface a message to the operator.
    // eslint-disable-next-line no-console
    console.log("[dp-post] click", {
      missing: missing.map((p) => p.code),
      canWrite,
      confirming,
      values,
    });
    if (!canWrite) {
      toast.error("You do not have permission to publish selling prices.");
      return;
    }
    if (missing.length > 0) {
      // Explicitly tell the operator WHICH product is missing.
      toast.error(missingMessage);
      return;
    }
    setPosting(true);
    try {
      const body = { lines: buildLines(), confirm_unusual: confirming };
      // eslint-disable-next-line no-console
      console.log("[dp-post] POST /daily-pricing/rack/batch", body);
      const { data } = await dpApi.postBatch(body);
      // eslint-disable-next-line no-console
      console.log("[dp-post] response", data);
      if (data?.requires_confirmation) {
        toast.info("Unusual changes detected — please review and confirm before posting.");
        setValidation(data.validation);
        setConfirming(true);
        return;
      }
      if (data?.posted) {
        toast.success(`Today's selling prices published (${data.posting.product_count} products).`);
        setValues(Object.fromEntries(PRODUCTS.map((p) => [p.code, ""])));
        setNotes("");
        setValidation(null);
        setConfirming(false);
        onPosted?.(data.posting);
        return;
      }
      // Backend returned 200 but didn't post — surface something.
      toast.error("Server did not publish today's selling prices. Please try again.");
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[dp-post] error", e);
      const d = e?.response?.data;
      const detail = d?.detail;
      if (detail && typeof detail === "object" && detail.errors) {
        setValidation({ ok: false, ...detail });
        toast.error(detail.errors.join(" · "));
      } else if (typeof detail === "string") {
        toast.error(detail);
      } else if (e?.message) {
        toast.error(`Posting failed: ${e.message}`);
      } else {
        toast.error("Posting failed. Please try again or reload the page.");
      }
    } finally {
      setPosting(false);
    }
  };

  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="dp-post-form">
      <header className="px-5 py-3 border-b border-slate-200">
        <h3 className="text-sm font-semibold text-slate-800">Publish Today's Selling Prices</h3>
        <p className="text-xs text-slate-500">
          Publish today's customer selling prices using the approved supplier rack prices. CAD per litre · all five products · single business action. Every publication is timestamped and audited.
        </p>
      </header>

      {!canWrite ? (
        <div className="p-4 text-sm text-slate-600" data-testid="dp-readonly-notice">
          Read-only view. Only Administrators and Owners may publish selling prices.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 p-5">
            {PRODUCTS.map((p) => {
              const previous = currentData?.items?.find((i) => i.product_code === p.code)?.current?.price_per_litre;
              return (
                <label key={p.code} className="text-sm">
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium text-slate-800">{p.label}</span>
                    <span className="text-[10px] text-slate-500">prev {previous ?? "—"}</span>
                  </div>
                  <div className="mt-1 flex items-center gap-1">
                    <span className="text-slate-400 text-xs">$</span>
                    <input
                      type="text"
                      inputMode="decimal"
                      value={values[p.code]}
                      onChange={(e) => setVal(p.code, e.target.value)}
                      placeholder="0.0000"
                      data-testid={`dp-input-${p.code}`}
                      className="w-full rounded border border-slate-300 px-2 py-1 font-mono tabular-nums"
                    />
                  </div>
                </label>
              );
            })}
          </div>

          <div className="px-5 pb-4">
            <label className="text-xs text-slate-600 block">
              Notes (optional)
              <textarea
                rows={2}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Supplier posted price sheet at 06:35; DEF adjusted for winter demand."
                data-testid="dp-notes"
                className="mt-1 w-full rounded border border-slate-300 px-2 py-1 text-sm"
              />
            </label>
          </div>

          {validation ? <ValidationPanel validation={validation} /> : null}

          <footer className="px-5 py-3 border-t border-slate-200 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={runValidate}
              disabled={!canValidate || validating}
              data-testid="dp-validate"
              className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
            >
              {validating ? "Checking…" : "Check before posting"}
            </button>
            <button
              type="button"
              onClick={runPost}
              disabled={!canWrite || posting || (validation?.warnings?.some((w) => w.kind === "unusual_change") && !confirming)}
              data-testid="dp-post"
              className="text-sm px-3 py-1.5 rounded bg-slate-900 text-white font-semibold hover:bg-slate-800 disabled:bg-slate-400 inline-flex items-center gap-1.5"
            >
              <PaperPlaneTilt size={14} />
              {posting ? "Publishing…" : "Publish Selling Prices"}
            </button>
            {missing.length > 0 ? (
              <span className="text-xs text-rose-700" data-testid="dp-missing-warn">
                {missingMessage}
              </span>
            ) : null}
            {validation?.warnings?.some((w) => w.kind === "unusual_change") ? (
              <label className="ml-auto text-xs inline-flex items-center gap-1 text-slate-700">
                <input
                  type="checkbox"
                  checked={confirming}
                  onChange={(e) => setConfirming(e.target.checked)}
                  data-testid="dp-confirm-unusual"
                />
                I've reviewed the unusually large changes above
              </label>
            ) : null}
          </footer>
        </>
      )}
    </section>
  );
}

function ValidationPanel({ validation }) {
  const hasErrors = validation.errors?.length > 0;
  const unusual   = (validation.warnings || []).filter((w) => w.kind === "unusual_change");
  const first     = (validation.warnings || []).filter((w) => w.kind === "first_posting");
  return (
    <div className="mx-5 mb-3 space-y-2" data-testid="dp-validation">
      {hasErrors ? (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-3 text-xs text-rose-900" data-testid="dp-validation-errors">
          <div className="font-semibold mb-1 flex items-center gap-1"><Warning size={14} weight="fill" /> Cannot post yet</div>
          <ul className="list-disc list-inside">
            {validation.errors.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </div>
      ) : null}
      {unusual.length > 0 ? (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-xs text-amber-900" data-testid="dp-validation-unusual">
          <div className="font-semibold mb-1 flex items-center gap-1"><Warning size={14} weight="fill" /> Unusually large changes</div>
          <ul className="list-disc list-inside">
            {unusual.map((w, i) => <li key={i}>{w.message}</li>)}
          </ul>
        </div>
      ) : null}
      {first.length > 0 ? (
        <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-700" data-testid="dp-validation-first">
          <div className="font-semibold mb-1 flex items-center gap-1"><Clock size={14} /> First posting</div>
          {first.length === PRODUCTS.length
            ? "This is the first selling price publication for all five products."
            : `${first.length} product(s) have no prior price to compare against.`}
        </div>
      ) : null}
      {!hasErrors && unusual.length === 0 && first.length === 0 ? (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-3 text-xs text-emerald-900" data-testid="dp-validation-clean">
          <div className="font-semibold flex items-center gap-1"><CheckCircle size={14} weight="fill" /> All changes look normal.</div>
        </div>
      ) : null}
    </div>
  );
}

/* ================================================================== */
/*  Section 4 — Today's Posting Summary                                */
/* ================================================================== */
function PostingSummary({ posting }) {
  if (!posting) {
    return (
      <section className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-5 text-sm text-slate-600" data-testid="dp-posting-empty">
        No selling prices published yet. Use the form above to publish today's prices.
      </section>
    );
  }
  return (
    <section className="rounded-xl border border-slate-200 bg-white shadow-sm p-5" data-testid="dp-posting-summary">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-wide text-emerald-700 font-semibold">Selling prices published</div>
          <h3 className="text-lg font-semibold text-slate-900">Today's Selling Prices Are Live</h3>
          <p className="text-sm text-slate-600 mt-1">
            {posting.product_count} product{posting.product_count === 1 ? "" : "s"} published by <b>{posting.posted_by}</b> · {fmtRel(posting.posted_at)}
          </p>
          <p className="text-xs text-slate-500 mt-1">Effective {fmtDT(posting.effective_at)}</p>
        </div>
        <div className="text-right">
          <span className="inline-flex items-center gap-1 bg-emerald-100 text-emerald-800 border border-emerald-300 px-2.5 py-1 rounded-full text-xs font-semibold">
            <CheckCircle size={14} weight="fill" /> Complete
          </span>
        </div>
      </div>
      {(posting.largest_increase || posting.largest_decrease) ? (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          {posting.largest_increase ? (
            <div className="rounded-lg border border-rose-100 bg-rose-50 p-3" data-testid="dp-largest-increase">
              <div className="text-xs uppercase text-rose-700 font-semibold">Largest increase</div>
              <div className="text-slate-900 font-medium mt-0.5">{posting.largest_increase.product_code}</div>
              <div className="text-slate-700 text-xs">
                {posting.largest_increase.previous} → {posting.largest_increase.current}
                <span className="ml-2 text-rose-700 font-semibold">+{posting.largest_increase.pct_change}%</span>
              </div>
            </div>
          ) : null}
          {posting.largest_decrease ? (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3" data-testid="dp-largest-decrease">
              <div className="text-xs uppercase text-emerald-700 font-semibold">Largest decrease</div>
              <div className="text-slate-900 font-medium mt-0.5">{posting.largest_decrease.product_code}</div>
              <div className="text-slate-700 text-xs">
                {posting.largest_decrease.previous} → {posting.largest_decrease.current}
                <span className="ml-2 text-emerald-700 font-semibold">{posting.largest_decrease.pct_change}%</span>
              </div>
            </div>
          ) : null}
        </div>
      ) : null}
      <p className="mt-4 text-xs text-slate-500">
        The pricing engine now serves today's rack via <code className="bg-slate-100 px-1 py-0.5 rounded">GET&nbsp;/api/daily-pricing/rack/current</code>. Sprint 2 will consume this contract to calculate customer selling prices automatically.
      </p>
    </section>
  );
}

/* ================================================================== */
/*  Root Daily Pricing tab                                             */
/* ================================================================== */
export default function DailyPricingTab({ canWrite }) {
  const [current, setCurrent] = useState(null);
  const [posting, setPosting] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: cur }, { data: lp }] = await Promise.all([dpApi.current(), dpApi.latest()]);
      setCurrent(cur);
      setPosting(lp?.posting || null);
    } catch (e) {
      toast.error("Failed to load daily pricing");
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="daily-pricing-tab">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-600 max-w-2xl">
          The daily selling-price publication step of the morning workflow. All five products, one submission, full audit.
          <span className="ml-1 text-slate-500">CAD per litre.</span>
        </p>
        <button
          type="button"
          onClick={load}
          data-testid="dp-refresh"
          className="text-xs text-slate-600 hover:text-slate-900 inline-flex items-center gap-1 font-medium"
        >
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>

      <CurrentSummary data={current} loading={loading} />
      <PostForm canWrite={canWrite} currentData={current} onPosted={(p) => { setPosting(p); load(); }} />
      <PostingSummary posting={posting} />
    </div>
  );
}
